/* eslint-disable ndte/intl-imports */

import { ReactNode, useMemo } from 'react';
import { createIntl, createIntlCache, IntlConfig, IntlShape, RawIntlProvider } from 'react-intl';

import { useSyncedRef } from 'hooks/use-synced-ref';

export function AppIntlProvider({ children, ...props }: IntlConfig & { children: ReactNode }) {
  const intl = useSetupIntl(props);

  return <RawIntlProvider value={intl}>{children}</RawIntlProvider>;
}

const intlCache = createIntlCache();

export function setupIntl(config: IntlConfig) {
  const enhanceOpts = (opts: any) => ({ calendar: 'gregory', ...opts });

  const intl = createIntl(config, intlCache);
  const customIntl: IntlShape = {
    ...intl,
    formatDate: (value, opts) => intl.formatDate(value, enhanceOpts(opts)),
    formatDateToParts: (value, opts) => intl.formatDateToParts(value, enhanceOpts(opts)),
    formatTime: (value, opts) => intl.formatTime(value, enhanceOpts(opts)),
    formatTimeToParts: (value, opts) => intl.formatTimeToParts(value, enhanceOpts(opts)),
    formatDateTimeRange: (from, to, opts) => intl.formatDateTimeRange(from, to, enhanceOpts(opts)),
  };

  return customIntl;
}

export function useSetupIntl(config: IntlConfig) {
  const onErrorCallback = useCallbackRef(config.onError || (() => {}));
  const onError = config.onError ? onErrorCallback : undefined;
  const finalConfig = { ...config, onError };

  return useMemo(
    () => setupIntl(finalConfig),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...Object.values(finalConfig)],
  );
}

function useCallbackRef<T extends CallableFunction>(fn: T) {
  const ref = useSyncedRef(fn);
  const callback = useMemo(() => {
    return (...args: any[]) => ref.current(...args);
  }, [ref]);

  return callback as any as T;
}
